import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  // message y Modal salen de App.useApp(); los estaticos no dibujan bajo React 19
  Space,
  Divider,
  TimePicker,
  Switch,
  Skeleton,
  Alert,
  Select,
  Tabs,
  App,
  Upload,
  Image,
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import {
  SaveOutlined,
  ShopOutlined,
  InfoCircleOutlined,
  PictureOutlined,
  UploadOutlined,
  DeleteOutlined,
  SettingOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../hooks/useResponsive';
import { useRestaurantTimeZone } from '../hooks/useTimeZone';
import {
  useGetRestaurantSettingsQuery,
  useUpdateRestaurantSettingsMutation,
  useUploadRestaurantLogoMutation,
  useDeleteRestaurantLogoMutation,
} from '../services/restaurantsApi';
import { useGetBoolSettingQuery, useGetIntSettingQuery, useUpdateSettingMutation } from '../services/settingsApi';
import { PRINT_ANIMATION_SETTING_KEY } from '../services/printAnimation';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const RestaurantSettings: React.FC = () => {
  const { message, modal } = App.useApp();
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [, setLogoFile] = useState<UploadFile | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  // Restaurant Settings API hooks
  const { data: settings, isLoading, error } = useGetRestaurantSettingsQuery();
  const [updateSettings, { isLoading: updating }] = useUpdateRestaurantSettingsMutation();
  const [uploadLogo, { isLoading: uploadingLogo }] = useUploadRestaurantLogoMutation();
  const [deleteLogo, { isLoading: deletingLogo }] = useDeleteRestaurantLogoMutation();

  // Get operations settings
  const { data: printTicketLiquidarSetting, refetch: refetchPrintTicketLiquidar } = useGetBoolSettingQuery('ImprimirTicket_LiquidarCuenta');
  const { data: printComandasSetting, refetch: refetchPrintComandas } = useGetBoolSettingQuery('ImprimirComandas');
  const { data: showTaxBreakdownSetting, refetch: refetchShowTaxBreakdown } = useGetBoolSettingQuery('MostrarDesgloseIVA');
  const { data: printAnimationSetting, refetch: refetchPrintAnimation } = useGetBoolSettingQuery(PRINT_ANIMATION_SETTING_KEY);
  const { data: businessDayCutoffSetting, refetch: refetchBusinessDayCutoff } = useGetIntSettingQuery('BusinessDayCutoffHour');
  const { data: menuDelDiaSetting, refetch: refetchMenuDelDia } = useGetBoolSettingQuery('MenuDelDiaActivo');
  const { data: cierreTurnoSetting, refetch: refetchCierreTurno } = useGetBoolSettingQuery('CierreTurnoActivo');
  const [updateSetting, { isLoading: settingSaving }] = useUpdateSettingMutation();

  // Load settings into form when data arrives
  useEffect(() => {
    if (settings) {
      form.setFieldsValue({
        name: settings.name,
        description: settings.description,
        address: settings.address,
        phone: settings.phone,
        email: settings.email,
        openTime: settings.openTime ? dayjs(settings.openTime, 'HH:mm:ss') : dayjs('09:00', 'HH:mm'),
        closeTime: settings.closeTime ? dayjs(settings.closeTime, 'HH:mm:ss') : dayjs('22:00', 'HH:mm'),
        isOpen: settings.isOpen,
        taxRate: settings.taxRate,
        currency: settings.currency,
      });
    }
  }, [settings, form]);

  // Load logo preview when settings load
  useEffect(() => {
    if (settings?.logoBase64 && settings?.logoMimeType) {
      const dataUrl = `data:${settings.logoMimeType};base64,${settings.logoBase64}`;
      setLogoPreview(dataUrl);
    } else {
      setLogoPreview(null);
    }
  }, [settings]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // Convert dayjs objects to TimeSpan strings (HH:mm:ss format)
      const updateData = {
        ...values,
        openTime: values.openTime.format('HH:mm:ss'),
        closeTime: values.closeTime.format('HH:mm:ss'),
      };

      await updateSettings(updateData).unwrap();

      message.success('Configuración guardada correctamente');
    } catch (error: any) {
      console.error('Error saving restaurant settings:', error);

      let errorMessage = 'Error al guardar la configuración';

      if (error?.status === 403) {
        errorMessage = 'No tienes permisos para modificar la configuración del restaurante. Contacta a un administrador.';
      } else if (error?.status === 401) {
        errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
      } else if (error?.status === 400) {
        errorMessage = error?.data?.message || 'Los datos proporcionados son inválidos';
      } else if (error?.status === 500) {
        errorMessage = 'Error del servidor. Por favor, intenta nuevamente más tarde.';
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      message.error(errorMessage, 5);
    }
  };

  const handleLogoUpload = async (file: File) => {
    try {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        message.error('Formato de imagen no válido. Use PNG, JPEG o WebP');
        return false;
      }

      // Validate file size (max 2MB)
      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        message.error('El tamaño de la imagen no debe exceder 2MB');
        return false;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = (reader.result as string).split(',')[1];

        try {
          await uploadLogo({
            logoBase64: base64,
            logoMimeType: file.type,
            logoNombreArchivo: file.name,
          }).unwrap();

          message.success('Logo subido correctamente');
          setLogoFile(null);
        } catch (error: any) {
          console.error('Error uploading logo:', error);
          message.error(error?.data?.message || 'Error al subir el logo');
        }
      };
      reader.readAsDataURL(file);

      return false; // Prevent default upload
    } catch (error: any) {
      console.error('Error processing logo:', error);
      message.error('Error al procesar la imagen');
      return false;
    }
  };

  const handleDeleteLogo = async () => {
    modal.confirm({
      title: '¿Eliminar logo?',
      content: '¿Está seguro de que desea eliminar el logo del restaurante?',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteLogo().unwrap();
          message.success('Logo eliminado correctamente');
          setLogoPreview(null);
          setLogoFile(null);
        } catch (error: any) {
          console.error('Error deleting logo:', error);
          message.error(error?.data?.message || 'Error al eliminar el logo');
        }
      },
    });
  };

  if (error) {
    let errorDescription = 'No se pudo cargar la configuración del restaurante';

    if ((error as any)?.status === 403) {
      errorDescription = 'No tienes permisos para ver la configuración del restaurante.';
    } else if ((error as any)?.status === 401) {
      errorDescription = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
    } else if ((error as any)?.data?.message) {
      errorDescription = (error as any).data.message;
    }

    return (
      <div>
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>
            <ShopOutlined /> Configuración del Restaurante
          </Title>
        </div>
        <Alert
          message="Error al cargar configuración"
          description={errorDescription}
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div>
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>
            <ShopOutlined /> Configuración del Restaurante
          </Title>
        </div>
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </div>
    );
  }

  return (
    <div>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/configuracion')}
        style={{ padding: 0, marginBottom: 16 }}
      >
        Volver a Configuración
      </Button>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <ShopOutlined /> Configuración del Restaurante
        </Title>
        <Text type="secondary">
          Configure la información básica y parámetros operacionales de su restaurante
        </Text>
      </div>

      <Card bodyStyle={{ padding: isMobile ? 12 : 24 }}>
        <Tabs
          defaultActiveKey="general"
          size={isMobile ? 'small' : 'middle'}
          items={[
            {
              key: 'general',
              label: (
                <span>
                  <InfoCircleOutlined />
                  {isMobile ? ' General' : ' Información General'}
                </span>
              ),
              children: (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Title level={4}>Información General</Title>
          <Divider />

          <Form.Item
            name="name"
            label="Nombre del Restaurante"
            rules={[{ required: true, message: 'Ingrese el nombre del restaurante' }]}
          >
            <Input size="large" />
          </Form.Item>

          <Form.Item name="description" label="Descripción">
            <TextArea rows={4} placeholder="Descripción del restaurante..." />
          </Form.Item>

          <Form.Item
            name="address"
            label="Dirección"
            rules={[{ required: true, message: 'Ingrese la dirección' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Teléfono"
            rules={[{ required: true, message: 'Ingrese el teléfono' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Ingrese el email' },
              { type: 'email', message: 'Email inválido' },
            ]}
          >
            <Input type="email" />
          </Form.Item>

          <Title level={4} style={{ marginTop: 32 }}>
            Horarios de Operación
          </Title>
          <Divider />

          <Space size="large" style={{ width: '100%' }} direction={isMobile ? 'vertical' : 'horizontal'}>
            <Form.Item name="openTime" label="Hora de Apertura">
              <TimePicker format="HH:mm" size="large" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="closeTime" label="Hora de Cierre">
              <TimePicker format="HH:mm" size="large" style={{ width: '100%' }} />
            </Form.Item>
          </Space>

          <Title level={4} style={{ marginTop: 32 }}>
            Parámetros Monetarios
          </Title>
          <Divider />

          <Space size="large" style={{ width: '100%' }} direction={isMobile ? 'vertical' : 'horizontal'}>
            <Form.Item
              name="taxRate"
              label="Tasa de Impuesto (%)"
              rules={[{ required: true, message: 'Ingrese la tasa de impuesto' }]}
            >
              <Input type="number" min={0} max={100} suffix="%" />
            </Form.Item>

            <Form.Item
              name="currency"
              label="Moneda"
              rules={[{ required: true, message: 'Ingrese la moneda' }]}
            >
              <Input placeholder="Bs., $, etc." />
            </Form.Item>
          </Space>

          <Divider />

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={updating}
              size="large"
              block={isMobile}
            >
              Guardar Configuración
            </Button>
          </Form.Item>
        </Form>
              ),
            },
            {
              key: 'branding',
              label: (
                <span>
                  <PictureOutlined />
                  {isMobile ? ' Marca' : ' Marca e Imagen'}
                </span>
              ),
              children: (
                <div>
                  <Title level={4}>Logo del Restaurante</Title>
                  <Text type="secondary">
                    Suba el logo de su restaurante. Este logo se utilizará en facturas, tickets y otros documentos.
                  </Text>
                  <Divider />

                  {logoPreview ? (
                    <div>
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <div>
                          <Text strong>Logo Actual:</Text>
                          <div style={{ marginTop: 12, textAlign: 'center' }}>
                            <Image
                              src={logoPreview}
                              alt="Logo del restaurante"
                              style={{ maxWidth: 300, maxHeight: 300 }}
                              preview={{
                                mask: 'Ver logo'
                              }}
                            />
                          </div>
                          {settings?.logoFechaSubida && (
                            <div style={{ marginTop: 8, textAlign: 'center' }}>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                Subido el {formatInTimeZone(settings.logoFechaSubida, 'DD/MM/YYYY')}
                              </Text>
                            </div>
                          )}
                        </div>

                        <Space>
                          <Upload
                            beforeUpload={handleLogoUpload}
                            showUploadList={false}
                            accept="image/png,image/jpeg,image/jpg,image/webp"
                          >
                            <Button
                              icon={<UploadOutlined />}
                              loading={uploadingLogo}
                              size="large"
                            >
                              Cambiar Logo
                            </Button>
                          </Upload>

                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={handleDeleteLogo}
                            loading={deletingLogo}
                            size="large"
                          >
                            Eliminar Logo
                          </Button>
                        </Space>
                      </Space>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <PictureOutlined style={{ fontSize: 64, color: '#d9d9d9', marginBottom: 16 }} />
                      <div style={{ marginBottom: 24 }}>
                        <Text type="secondary">No hay logo configurado</Text>
                      </div>
                      <Upload
                        beforeUpload={handleLogoUpload}
                        showUploadList={false}
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                      >
                        <Button
                          type="primary"
                          icon={<UploadOutlined />}
                          loading={uploadingLogo}
                          size="large"
                        >
                          Subir Logo
                        </Button>
                      </Upload>
                    </div>
                  )}

                  <Divider />

                  <Alert
                    message="Requisitos del Logo"
                    description={
                      <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                        <li>Formatos soportados: PNG, JPEG, WebP</li>
                        <li>Tamaño máximo: 2MB</li>
                        <li>Recomendado: Imagen cuadrada con fondo transparente (PNG)</li>
                        <li>Dimensiones recomendadas: 500x500 px o superior</li>
                      </ul>
                    }
                    type="info"
                    showIcon
                  />
                </div>
              ),
            },
            {
              key: 'operations',
              label: (
                <span>
                  <SettingOutlined />
                  {isMobile ? '' : ' Operaciones'}
                </span>
              ),
              children: (
                <div>
                  <Title level={4}>Configuraciones Operativas</Title>
                  <Text type="secondary">
                    Configura el comportamiento operativo del sistema de punto de venta
                  </Text>
                  <Divider />

                  <Form layout="vertical">
                    <Title level={5}>Módulos</Title>
                    <Form.Item
                      label="Menú del Día"
                      extra="Habilita el módulo de Menú del Día para configurar menús diarios con tiempos y opciones que cambian cada día"
                    >
                      <Switch
                        checked={menuDelDiaSetting?.value ?? false}
                        onChange={async (checked) => {
                          try {
                            await updateSetting({
                              key: 'MenuDelDiaActivo',
                              value: checked.toString()
                            }).unwrap();
                            message.success(checked ? 'Menú del Día activado' : 'Menú del Día desactivado');
                            refetchMenuDelDia();
                          } catch (error) {
                            message.error('Error al guardar configuración');
                          }
                        }}
                        loading={settingSaving}
                        checkedChildren="Activo"
                        unCheckedChildren="Inactivo"
                      />
                    </Form.Item>

                    <Divider />
                    <Title level={5}>Impresión de Tickets</Title>
                    <Form.Item
                      label="Imprimir ticket al liquidar cuenta"
                      extra="Define si el checkbox de imprimir ticket está marcado por defecto al liquidar una cuenta"
                    >
                      <Switch
                        checked={printTicketLiquidarSetting?.value ?? true}
                        onChange={async (checked) => {
                          try {
                            await updateSetting({
                              key: 'ImprimirTicket_LiquidarCuenta',
                              value: checked.toString()
                            }).unwrap();
                            message.success('Configuración guardada');
                            refetchPrintTicketLiquidar();
                          } catch (error) {
                            message.error('Error al guardar configuración');
                          }
                        }}
                        loading={settingSaving}
                        checkedChildren="Sí"
                        unCheckedChildren="No"
                      />
                    </Form.Item>

                    <Form.Item
                      label="Animación al imprimir"
                      extra="Muestra en pantalla una impresora sacando el ticket al imprimir precuentas, cuentas y cortes de caja. Las comandas no la usan. Apagada por defecto para no frenar a la caja en hora pico."
                      style={{ marginTop: 16 }}
                    >
                      <Switch
                        checked={printAnimationSetting?.value ?? false}
                        onChange={async (checked) => {
                          try {
                            await updateSetting({
                              key: PRINT_ANIMATION_SETTING_KEY,
                              value: checked.toString()
                            }).unwrap();
                            message.success('Configuración guardada');
                            refetchPrintAnimation();
                          } catch (error) {
                            message.error('Error al guardar configuración');
                          }
                        }}
                        loading={settingSaving}
                        checkedChildren="Sí"
                        unCheckedChildren="No"
                      />
                    </Form.Item>

                    <Title level={5} style={{ marginTop: 24 }}>Impresión de Comandas</Title>
                    <Form.Item
                      label="Imprimir comandas al enviar a cocina"
                      extra="Si está desactivado, las comandas solo aparecerán en la pantalla de cocina (KDS) sin imprimirse"
                    >
                      <Switch
                        checked={printComandasSetting?.value ?? true}
                        onChange={async (checked) => {
                          try {
                            await updateSetting({
                              key: 'ImprimirComandas',
                              value: checked.toString()
                            }).unwrap();
                            message.success('Configuración guardada');
                            refetchPrintComandas();
                          } catch (error) {
                            message.error('Error al guardar configuración');
                          }
                        }}
                        loading={settingSaving}
                        checkedChildren="Sí"
                        unCheckedChildren="No"
                      />
                    </Form.Item>

                    <Form.Item
                      label="Mostrar desglose de IVA en tickets"
                      extra="Muestra las líneas de Subtotal e IVA en los tickets de precuenta y pago. Si se desactiva, solo se muestra el Total."
                      style={{ marginTop: 16 }}
                    >
                      <Switch
                        checked={showTaxBreakdownSetting?.value ?? true}
                        onChange={async (checked) => {
                          try {
                            await updateSetting({
                              key: 'MostrarDesgloseIVA',
                              value: checked.toString()
                            }).unwrap();
                            message.success('Configuración guardada');
                            refetchShowTaxBreakdown();
                          } catch (error) {
                            message.error('Error al guardar configuración');
                          }
                        }}
                        loading={settingSaving}
                        checkedChildren="Sí"
                        unCheckedChildren="No"
                      />
                    </Form.Item>

                    <Divider />
                    <Title level={5}>Caja Registradora</Title>
                    <Form.Item
                      label="Hora de corte del día de negocio"
                      extra="Hora en que inicia un nuevo día de operación. Para bares/antros que cierran de madrugada, usar 6 AM para que el turno nocturno se considere del mismo día."
                    >
                      <Select
                        value={businessDayCutoffSetting?.value ?? 0}
                        onChange={async (value) => {
                          try {
                            await updateSetting({
                              key: 'BusinessDayCutoffHour',
                              value: value.toString()
                            }).unwrap();
                            message.success('Configuración guardada');
                            refetchBusinessDayCutoff();
                          } catch (error) {
                            message.error('Error al guardar configuración');
                          }
                        }}
                        style={{ width: 280 }}
                        loading={settingSaving}
                      >
                        <Select.Option value={0}>12:00 AM (Medianoche) - Default</Select.Option>
                        <Select.Option value={1}>1:00 AM</Select.Option>
                        <Select.Option value={2}>2:00 AM</Select.Option>
                        <Select.Option value={3}>3:00 AM</Select.Option>
                        <Select.Option value={4}>4:00 AM</Select.Option>
                        <Select.Option value={5}>5:00 AM</Select.Option>
                        <Select.Option value={6}>6:00 AM (Recomendado para bares)</Select.Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label="Habilitar Cierre de Turno"
                      extra="Activa la funcionalidad de Cierre de Turno: muestra 'Cierres de Turno' en el menú lateral y el botón de cierre de turno en la caja. Desactivado por defecto."
                      style={{ marginTop: 16 }}
                    >
                      <Switch
                        checked={cierreTurnoSetting?.value ?? false}
                        onChange={async (checked) => {
                          try {
                            await updateSetting({
                              key: 'CierreTurnoActivo',
                              value: checked.toString()
                            }).unwrap();
                            message.success('Configuración guardada');
                            refetchCierreTurno();
                          } catch (error) {
                            message.error('Error al guardar configuración');
                          }
                        }}
                        loading={settingSaving}
                        checkedChildren="Sí"
                        unCheckedChildren="No"
                      />
                    </Form.Item>
                  </Form>
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default RestaurantSettings;
